import {
  appTimeBundle,
  composeBundlesRaw,
  createCacheBundle,
  createDebugBundle,
  createSelector,
  createUrlBundle,
} from 'redux-bundler'

import detectLocale from 'browser-locale'
import queryString from 'query-string'

import { accountSubscription, activeDeviceList } from '~/src/Account/Management/bundle'
import appConfig from '~/src/App/config'
import comments from '~/src/Comments/bundle'
import { eventList, events, eventStats } from '~/src/Compliance/bundle'
import {
  cultivarKpis,
  cultivarKpiProfile,
  cultivarList,
  cultivars,
  cultivarStatistic,
  cultivarStatisticList,
  kpis,
  kpisList,
  kpiProfiles,
  kpiProfilesList,
  kpiRecipes,
} from '~/src/Cultivars/bundle'
import dataTypes from '~/src/DataType/bundle'
import { targetRanges, targetRangesList } from '~/src/DataType/TargetRange/bundle'
import {
  deviceList,
  deviceProvisioning,
  devices,
  foundDevices,
  provisioningStatus,
} from '~/src/Device/bundle'
import dialog from '~/src/Dialog/bundle'
import drawer from '~/src/Drawer/bundle'
import facilities, {
  facilityChart,
  facilityEfficiency,
  facilityInventory,
} from '~/src/Facility/bundle'
import facilityPhases from '~/src/Facility/bundle/facilityPhases'
import floorplans from '~/src/Facility/bundle/floorplans'
import latestReadings from '~/src/Facility/bundle/readings'
import flags from '~/src/Flags/bundle'
import drying from '~/src/Flow/bundle/drying'
import flow from '~/src/Flow/bundle/general'
// import flows from '~/src/Flow/bundle'
import harvesting from '~/src/Flow/bundle/harvesting'
import processing from '~/src/Flow/bundle/processing'
import processingInventory from '~/src/Flow/bundle/processing/inventory'
// Entity bundles
import { bundles as harvestBundles } from '~/src/Harvest/bundle'
import growingHarvests from '~/src/HarvestPhaseChange/bundle'
import { inventoryList } from '~/src/Inventory/bundle'
import getApiFetch from '~/src/IO/Api'
import cache from '~/src/IO/Cache'
import pesticideApplications, { list as pesticideApplicationsList } from '~/src/IPM/bundle'
import pesticides, { list as pesticidesList } from '~/src/IPM/Pesticide/bundle'
import {
  facilityIrrigationSchedules,
  irrigationControllers,
  irrigationControllersList,
  irrigationProgramHistory,
  irrigationScheduleChangeNotificationList,
  irrigationScheduleChangeNotifications,
  irrigationSchedules,
  irrigationSchedulesList,
  irrigationScheduleTemplates,
  modbusRegisters,
  roomIrrigationSchedules,
} from '~/src/Irrigation/bundle'
import items, { list as itemsList } from '~/src/Item/bundle'
import journal from '~/src/Journal/bundle'
import { labType, labTypeList } from '~/src/Lab/bundle'
import metrc from '~/src/Metrc/bundle'
import { notes } from '~/src/Note/bundle'
import { harvestBatches, packages } from '~/src/Package/bundle'
import { plantBatches, plants, replacePlantInventory } from '~/src/Plant/bundle'
import readings from '~/src/Readings/ManualReading/bundle'
import { recipeList, recipes } from '~/src/Recipe/bundle'
import {
  lastHarvest,
  nextHarvest,
  roomChart,
  roomCultivarsList,
  roomHarvestsList,
  roomInventory,
  roomList,
  rooms,
} from '~/src/Room/bundle'
import roomDashboard from '~/src/Room/Dashboard/bundle'
import { lightSchedules, lightSchedulesList } from '~/src/Room/LightSchedule/bundle'
import { substrateSettings } from '~/src/Room/SubstrateSettings/bundle'
import routes from '~/src/Routes/bundle'
import { logger, loggerList } from '~/src/Setup/bundle'
import grades, { list as gradesList } from '~/src/Setup/Grades/bundle'
import { membershipList, memberships, notificationPreferences } from '~/src/Staff/Membership/bundle'
import facilityRoles from '~/src/Staff/Role/bundle'
import runs from '~/src/Strainrun/Analytics/bundle'
import support from '~/src/Support/bundle'
import { tasks } from '~/src/Task/bundle'
import { save } from '~/vendor/redux-localstorage-simple'

import badges from '../App/bundle/badges'
import { HARVESTING_FLOW } from '../Flow/constants'
import audio from './bundles/audio'
// System bundles
import auth from './bundles/auth'
import barcode from './bundles/barcode'
import chart, { annotations } from './bundles/chart'
import config from './bundles/config'
import dimensions from './bundles/dimensions'
import prepareEntityBundles from './bundles/entities'
import labelsList from './bundles/labels'
import me from './bundles/me'
import currentFacility from './bundles/me/currentFacility'
import onlineBundle from './bundles/online'
import reactor from './bundles/reactor'
import scale from './bundles/scale'
import snackbar from './bundles/snackbar'
import sockets from './bundles/sockets'
import system from './bundles/system'
import tabState from './bundles/tabState'
import thirdParty from './bundles/thirdParty'
import userSettings from './bundles/userSettings'
import { URL_ACTION } from './constants'

const cacheBundle = createCacheBundle({
  cacheFn: cache.set,
  logger: cache.logger,
})

const extraArgs = {
  name: 'extraArgs',
  getMiddleware: () => save({
    states: [HARVESTING_FLOW, 'userSettings'],
    namespace: `__${appConfig.APP}__`,
    debounce: 1000,
  }),
  getExtraArgs: store => ({ apiFetch: getApiFetch(store) }),
  doApiFetch: (...args) => ({ apiFetch }) => apiFetch(...args),
  doUpdateQueryAdvanced: (payload, opts = { replace: false }) => ({ store }) => {
    const search = queryString.stringify(payload)
    const oldSearch = store.selectUrlObject().search
    if (search !== oldSearch) {
      store.doUpdateQuery(search, opts)
    }
  },
  selectQueryAdvanced: createSelector(
    'selectUrlObject',
    ({ search }) => queryString.parse(search, { parseNumbers: true, parseBooleans: true })
  ),
}

const locale = {
  name: 'locale',
  reducer: (state = detectLocale()) => state,
  selectLocale: state => state.locale,
}

const isIOPattern = /sav(e|ing)|creat(e|ing)|delet(e|ing)|destroy|updat(e|ing)|fetch/i
const isStartPattern = /start/i
const isReadPattern = /fetch/i
const io = {
  name: 'io',
  reducer: (state = { inflight: { read: 0, write: 0 } }, action = {}) => {
    if (!action.type || !isIOPattern.test(action.type)) return state
    const {
      inflight: { read, write },
    } = state
    const isStart = isStartPattern.test(action.type)
    const isRead = isReadPattern.test(action.type)
    const newState = {
      inflight: {
        read: isRead ? Math.max(0, isStart ? read + 1 : read - 1) : read,
        write: isRead ? write : Math.max(0, isStart ? write + 1 : write - 1),
      },
    }
    return newState
  },
  selectInflight: state => state.io && state.io.inflight,
}

const MAINTENANCE_MODE_ENABLED = 'MAINTENANCE_MODE_ENABLED'
const MAINTENANCE_MODE_DISABLED = 'MAINTENANCE_MODE_DISABLED'

const maintenanceMode = {
  name: 'maintenanceMode',
  reducer: (state = false, action = {}) => {
    switch (action.type) {
      case MAINTENANCE_MODE_ENABLED:
        return true
      case MAINTENANCE_MODE_DISABLED:
        return false
      default:
        return state
    }
  },
  doMaintenanceModeEnable: () => ({ dispatch }) => dispatch({ type: MAINTENANCE_MODE_ENABLED }),
  doMaintenanceModeDisable: () => ({ dispatch }) => dispatch({ type: MAINTENANCE_MODE_DISABLED }),
  selectMaintenanceModeEnabled: state => state.maintenanceMode,
}

const noLogActions = {
  APP_IDLE: 1,
  SET_URL_HISTORY: 1,
  DIMENSIONS_UPDATED: 1,
}

const { 0: harvests, [harvestBundles.length - 1]: phases } = harvestBundles
const entityBundles = [
  cultivars,
  cultivarStatistic,
  kpis,
  kpiProfiles,
  kpiRecipes,
  dataTypes,
  devices,
  events,
  facilities,
  facilityPhases,
  facilityRoles,
  floorplans,
  grades,
  harvestBatches,
  harvests,
  items,
  irrigationSchedules,
  irrigationProgramHistory,
  labType,
  lightSchedules,
  logger,
  memberships,
  notes,
  packages,
  phases,
  plantBatches,
  plants,
  replacePlantInventory,
  pesticideApplications,
  pesticides,
  readings,
  recipes,
  rooms,
  substrateSettings,
  targetRanges,
  tasks
]
const urlBundle = {
  ...createUrlBundle({
    actionType: URL_ACTION,
  }),
  doDownload: url => ({ apiFetch }) => apiFetch(url, null, { download: true })
}
const appReady = {
  name: 'appReady',
  selectAppIsReady: createSelector(
    'selectAuth',
    'selectCurrentFacilityId',
    'selectMe',
    'selectSystem',
    ({ authenticated }, currentFacilityId, meData, systemData) => [
      authenticated,
      currentFacilityId,
      Array.isArray(meData?.memberships),
      Array.isArray(systemData?.permissions),
    ].every(Boolean)
  ),
}

export default composeBundlesRaw(
  // redux-bundler built-ins
  appTimeBundle,
  onlineBundle,
  createDebugBundle({
    logSelectors: false,
    logState: false,
    actionFilter: action => action && !(action.type in noLogActions)
  }),
  reactor,
  thirdParty,
  urlBundle,
  // System bundles
  appReady,
  audio,
  auth,
  barcode,
  config,
  currentFacility,
  dialog,
  drawer,
  flags,
  routes,
  scale,
  sockets,
  dimensions,
  cacheBundle,
  extraArgs,
  locale,
  snackbar,
  io,
  maintenanceMode,
  support,
  tabState,
  // Platform data bundles
  comments,
  accountSubscription,
  activeDeviceList,
  provisioningStatus,
  annotations,
  badges,
  chart,
  deviceProvisioning,
  eventStats,
  foundDevices,
  journal,
  growingHarvests,
  latestReadings,
  irrigationControllers,
  irrigationControllersList,
  modbusRegisters,
  irrigationScheduleChangeNotificationList,
  irrigationScheduleChangeNotifications,
  irrigationScheduleTemplates,
  irrigationSchedulesList,
  lightSchedulesList,
  targetRangesList,
  metrc,
  // flows,
  harvesting,
  drying,
  processing,
  flow,
  // harvest bundles
  ...harvestBundles.slice(1, -1),
  // Entity bundles
  me,
  userSettings,
  system,
  notificationPreferences,
  ...prepareEntityBundles(entityBundles),
  // auto-updating async entity bundles
  lastHarvest,
  nextHarvest,
  // List bundles
  cultivarList,
  deviceList,
  facilityIrrigationSchedules,
  gradesList,
  roomCultivarsList,
  inventoryList,
  itemsList,
  labelsList,
  membershipList,
  pesticideApplicationsList,
  pesticidesList,
  processingInventory,
  recipeList,
  roomChart,
  roomInventory,
  roomIrrigationSchedules,
  roomHarvestsList,
  roomList,
  eventList,
  labTypeList,
  loggerList,
  cultivarStatisticList,
  cultivarKpis,
  cultivarKpiProfile,
  kpisList,
  kpiProfilesList,
  runs,
  // Page bundles
  roomDashboard,
  facilityChart,
  facilityEfficiency,
  facilityInventory,
)
